// src/modules/products/hooks/useProductForm.ts

import { useState, useEffect, useMemo, useCallback } from "react";
import { ProductItem } from "../models/product.model";
import {
  stringifyOptions,
  convertPriceByUnit,
} from "@modules/shared/utils/formatters";
import { ProductImageFormItem, ProductFormState } from "../types/product.types";

const createImageId = () =>
  `img-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`;

const mapImagesToItems = (images: string[]): ProductImageFormItem[] =>
  images.map((url) => ({ id: createImageId(), url, source: "existing" }));

const createImageItem = (file: File): ProductImageFormItem => ({
  id: createImageId(),
  url: URL.createObjectURL(file),
  name: file.name,
  file,
  source: "new",
});

const createFormState = (product?: ProductItem): ProductFormState => ({
  name: product?.name ?? "",
  shop_id: product?.shop_id ?? "",
  category: product?.category?.trim() ?? "",
  price_unit: product?.price_unit ?? "VND",
  price: product?.price ?? "0",
  stock: String(product?.stock ?? 0),
  bought: String(product?.bought ?? 0),
  status: product?.status ?? "TRACKER",
  payment_method: product?.payment_method ?? "CONTACT",
  discount_percentage: String(product?.discount_percentage ?? 0),
  description_en: product?.description.en ?? "",
  description_vi: product?.description.vi ?? "",
  origin: product?.detail_information.origin ?? "",
  send_from: product?.detail_information.send_from ?? "",
  warranty: product?.detail_information.warranty ?? null,
  warranty_unit: product?.detail_information.warranty_unit ?? "",
  optionsText: stringifyOptions(product?.options ?? []),
  images: mapImagesToItems(product?.images ?? []),
});

export const useProductForm = (initialProduct?: ProductItem) => {
  const [formState, setFormState] = useState<ProductFormState>(() =>
    createFormState(initialProduct),
  );

  useEffect(() => {
    setFormState(createFormState(initialProduct));
  }, [initialProduct]);

  const existingImages = useMemo(
    () =>
      formState.images
        .filter((img) => img.source === "existing")
        .map((img) => img.url),
    [formState.images],
  );
  const newImageFiles = useMemo(
    () =>
      formState.images
        .filter((img) => img.source === "new" && img.file)
        .map((img) => img.file as File),
    [formState.images],
  );
  const galleryUrls = useMemo(
    () => formState.images.map((img) => img.url).filter(Boolean),
    [formState.images],
  );

  const handleFieldChange = useCallback(
    (field: keyof ProductFormState, value: string) => {
      setFormState((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleImageFilesAdd = useCallback((files: File[]) => {
    const newItems = files.map(createImageItem);
    setFormState((prev) => ({
      ...prev,
      images: [...prev.images, ...newItems],
    }));
  }, []);

  const handleRemoveImage = useCallback((id: string) => {
    setFormState((prev) => ({
      ...prev,
      images: prev.images.filter((img) => {
        if (img.id === id && img.source === "new" && img.url.startsWith("blob:")) {
          URL.revokeObjectURL(img.url);
        }
        return img.id !== id;
      }),
    }));
  }, []);

  const handleMakeCover = useCallback((id: string) => {
    setFormState((prev) => {
      const selected = prev.images.find((img) => img.id === id);
      if (!selected) return prev;
      return {
        ...prev,
        images: [selected, ...prev.images.filter((img) => img.id !== id)],
      };
    });
  }, []);

  const handlePriceUnitChange = useCallback((newUnit: string) => {
    setFormState((prev) => ({
      ...prev,
      price_unit: newUnit,
      price: convertPriceByUnit(prev.price, prev.price_unit, newUnit),
    }));
  }, []);

  const setWarranty = useCallback((value: number | null) => {
    setFormState((prev) => ({ ...prev, warranty: value }));
  }, []);

  const resetForm = useCallback((product?: ProductItem) => {
    setFormState(createFormState(product));
  }, []);

  return {
    formState,
    setFormState,
    existingImages,
    newImageFiles,
    galleryUrls,
    handleFieldChange,
    handleImageFilesAdd,
    handleRemoveImage,
    handleMakeCover,
    handlePriceUnitChange,
    setWarranty,
    resetForm,
  };
};
